import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SaveOutlined,
  RefreshOutlined,
  WarningAmberOutlined,
  CloseOutlined,
} from '@mui/icons-material';
import authService from '../../../services/authService';
import ModuleCard from '../ModuleCard/ModuleCard';
import ConfirmPopup from '../../molecules/ConfirmPopup/ConfirmPopup';
import { buildInitialPerm, getAllWarnings, syncLinkedForecastIntervals, getLinkedForecastCatalogId } from '../../../utils/accessRightsHelpers';
import { FORECAST_MIN_DAY, FORECAST_MAX_DAY, FRANCE_COUNTRY_ID } from '../../../constants/accessRightsConfig';
import './AccessRightsModal.css';
import { showToast } from '../../atoms/Toast/Toast';

const AccessRightsModal = ({ user, currentUser, onClose, onSave, onRevoke }) => {
  const [modules,        setModules]        = useState([]);
  const [permissions,    setPermissions]    = useState({});
  const [loading,        setLoading]        = useState(true);
  const [isSaving,       setIsSaving]       = useState(false);
  const [isRevoking,     setIsRevoking]     = useState(false);
  const [error,          setError]          = useState(null);
  const [showRevoke,     setShowRevoke]     = useState(false);
  const [disableConfirm, setDisableConfirm] = useState(null);

  // ── Confirmation email après sauvegarde des droits (existant) ──
  const [pendingRightsConfirm, setPendingRightsConfirm] = useState(null);

  // ── Avertissements de config incomplète avant sauvegarde (existant) ──
  const [pendingWarnings, setPendingWarnings] = useState(null);

  // ── NOUVEAU : confirmation de la modification des droits, avant tout appel API ──
  const [pendingRightsSubmit, setPendingRightsSubmit] = useState(null);

  // ── NOUVEAU : liste de référence des départements français, utilisée pour
  // l'affichage statique "Points chauds" (module sans données pays/départements
  // propres) — prise sur le premier module qui possède réellement la France
  // avec ses départements.
  const [frenchDepartmentsCatalog, setFrenchDepartmentsCatalog] = useState([]);

  const dirtyRef     = useRef(new Set());
  const isSuperAdmin = currentUser?.role === 'superadmin';

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && !showRevoke && !disableConfirm && !pendingWarnings && !pendingRightsConfirm && !pendingRightsSubmit) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, showRevoke, disableConfirm, pendingWarnings, pendingRightsConfirm, pendingRightsSubmit]);

  // ── FETCH ────────────────────────────────────────────────────────────────
  const fetchRights = useCallback(async () => {
    setLoading(true); setError(null); dirtyRef.current = new Set();
    try {
      const data   = await authService.getUserRights(user.id);
      const sorted = (data.modules || []).sort((a, b) => a.catalog_id - b.catalog_id);
      setModules(sorted);
      const perms = {};
      sorted.forEach((mod) => { perms[mod.catalog_id] = buildInitialPerm(mod); });
      // ── Intervalle de prévision partagé Météo ↔ Couches terrain : on aligne
      // les deux modules sur la valeur explicitement configurée au chargement.
      syncLinkedForecastIntervals(sorted, perms);
      setPermissions(perms);

      // ── Référence départements France, pour l'affichage statique "Points chauds" ──
      const deptSourceCountry = sorted
        .map((mod) => mod.countries?.find((c) => c.id === FRANCE_COUNTRY_ID))
        .find((c) => c?.departements?.length > 0);
      setFrenchDepartmentsCatalog(deptSourceCountry?.departements || []);
    } catch (e) {
      setError(e.message || 'Erreur lors du chargement des droits');
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { fetchRights(); }, [fetchRights]);

  // ── HANDLERS ─────────────────────────────────────────────────────────────
  const handleChange = useCallback((catalogId, field, value) => {
    setPermissions((prev) => {
      const next = { ...prev, [catalogId]: { ...prev[catalogId], [field]: value } };

      // ── Intervalle de prévision partagé Météo ↔ Couches terrain ──
      // Modifier l'intervalle sur l'un des deux modules propage la même
      // valeur sur l'autre, qu'il soit affiché ou non à l'écran.
      if (field === 'intervalMinJ' || field === 'intervalMaxJ') {
        const linkedId = getLinkedForecastCatalogId(modules, catalogId);
        if (linkedId != null && next[linkedId]) {
          next[linkedId] = { ...next[linkedId], [field]: value };
        }
      }

      return next;
    });

    dirtyRef.current.add(catalogId);

    // Le module jumeau doit aussi être inclus dans la sauvegarde puisque
    // sa valeur vient d'être modifiée en même temps.
    if (field === 'intervalMinJ' || field === 'intervalMaxJ') {
      const linkedId = getLinkedForecastCatalogId(modules, catalogId);
      if (linkedId != null) dirtyRef.current.add(linkedId);
    }
  }, [modules]);

  const handleRequestDisable = (catalogId, label) => setDisableConfirm({ catalogId, label });

  const confirmDisable = () => {
    if (!disableConfirm) return;
    handleChange(disableConfirm.catalogId, 'enabled', false);
    setDisableConfirm(null);
  };

  // ── SAVE réelle — logique existante inchangée (mapping + appel API + confirmation email) ──
  const performSave = async () => {
    setIsSaving(true); setError(null);
    try {
      const rights = modules
        .filter((mod) => dirtyRef.current.has(mod.catalog_id) && (mod.can_grant || isSuperAdmin))
        .map((mod) => {
          const perm = permissions[mod.catalog_id];
          const availLayers  = mod.available_layer_ids?.length      > 0 ? mod.available_layer_ids      : null;
          const availCountry = mod.available_country_ids?.length    > 0 ? mod.available_country_ids    : null;
          const availDept    = mod.available_department_ids?.length > 0 ? mod.available_department_ids : null;
          const availMinJ    = mod.available_interval_min_j ?? FORECAST_MIN_DAY;
          const availMaxJ    = mod.available_interval_max_j ?? FORECAST_MAX_DAY;
          const resolve = (selected, available, allIds) => {
            const base = selected?.length ? selected : allIds;
            return available ? base.filter(id => available.includes(id)) : base;
          };
          const allLayerIds      = (mod.layers    || []).map(l => l.id);
          const allCountryIds    = (mod.countries || []).map(c => c.id);
          const franceCountry    = mod.countries?.find(c => c.id === FRANCE_COUNTRY_ID);
          const allDepartmentIds = franceCountry?.departements?.map(d => d.id) || [];
          const selectedLayerIds   = resolve(perm.selectedLayerIds,   availLayers,  allLayerIds);
          const selectedCountryIds = resolve(perm.selectedCountryIds, availCountry, allCountryIds);
          const isFranceIncluded   = selectedCountryIds.includes(FRANCE_COUNTRY_ID);
          const selectedDepartmentIds = isFranceIncluded
            ? resolve(perm.selectedDepartmentIds, availDept, allDepartmentIds)
            : [];
          const intervalMinJ = Math.max(availMinJ, Math.min(0, perm.intervalMinJ ?? 0));
          const intervalMaxJ = Math.max(0, Math.min(availMaxJ, perm.intervalMaxJ ?? 0));
          const entry = {
            right_id:                mod.catalog_id,
            active:                  perm.enabled,
            selected_layer_ids:      selectedLayerIds,
            selected_country_ids:    selectedCountryIds,
            selected_department_ids: selectedDepartmentIds,
            interval_min_j:          intervalMinJ,
            interval_max_j:          intervalMaxJ,
          };

          // ── Historique des feux : seul module avec une vraie plage de dates.
          // On envoie toujours date_start/date_end explicitement (null si
          // effacé) plutôt que d'omettre la clé — sinon un champ manquant
          // est interprété côté backend comme "ne pas modifier", et effacer
          // la date dans l'UI ne l'efface jamais réellement côté serveur.
          const modServiceName = mod.services?.[0]?.name || '';
          if (modServiceName === 'historic_fire_service') {
            entry.date_start = perm.dateStart instanceof Date ? perm.dateStart.toISOString() : null;
            entry.date_end   = perm.dateEnd   instanceof Date ? perm.dateEnd.toISOString()   : null;
          }

          return entry;
        });

      if (rights.length === 0) { setIsSaving(false); return; }

      const response = await authService.updateUserRights(user.id, rights);
      onSave?.(user.id);

      // ── confirmation email (existant, inchangé) ──
      if (response?.data?.pending_email_confirmation) {
        setPendingRightsConfirm({ userId: user.id, username: user.username });
      } else {
        showToast('Droits d\'accès mis à jour', 'success');
        onClose();
      }
    } catch (e) {
      setError(e.message || 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  // ── SAVE — point d'entrée du bouton : vérifie d'abord les avertissements de config ──
  const handleSave = () => {
    if (dirtyRef.current.size === 0) return;

    const dirtyIds = new Set(dirtyRef.current);
    const warningsList = getAllWarnings(modules, permissions, dirtyIds);

    if (warningsList.length > 0) {
      setPendingWarnings(warningsList);
      return;
    }

    // ── Pas de warning → demande d'abord confirmation de la modification ──
    setPendingRightsSubmit(true);
  };

  const handleConfirmSaveAnyway = () => {
    setPendingWarnings(null);
    // ── Après avoir accepté malgré les warnings, demander confirmation ──
    setPendingRightsSubmit(true);
  };

  const handleCancelSaveWarnings = () => {
    setPendingWarnings(null);
  };

  // ── NOUVEAU : confirmation de la modification des droits, avant appel API ──
  const handleConfirmRightsSubmit = async () => {
    setPendingRightsSubmit(null);
    await performSave();
  };

  const handleCancelRightsSubmit = () => {
    setPendingRightsSubmit(null);
  };

  // ── Confirmation email après sauvegarde (existant, inchangé) ──
  const handleConfirmRightsEmail = async () => {
    try {
      await authService.confirmRightsEmail(pendingRightsConfirm.userId);
      showToast('Email de droits envoyé !', 'success');
    } catch {
      showToast("Erreur lors de l'envoi.", 'error');
    } finally {
      setPendingRightsConfirm(null);
      onClose();
    }
  };

  const handleCancelRightsEmail = () => {
    setPendingRightsConfirm(null);
    showToast('Droits mis à jour sans email.', 'success');
    onClose();
  };

  // ── REVOKE ───────────────────────────────────────────────────────────────
  const handleRevoke = async () => {
    setIsRevoking(true); setError(null);
    try {
      await authService.revokeUserRights(user.id);
      showToast('Droits d\'accès révoqués avec succès', 'success');
      onRevoke?.(user.id);
      onClose();
    } catch (e) {
      setError(e.message || 'Erreur lors de la révocation');
      setShowRevoke(false);
    } finally {
      setIsRevoking(false);
    }
  };

  const hasChanges   = dirtyRef.current.size > 0;
  const enabledCount = Object.values(permissions).filter((p) => p.enabled).length;

  return (
    <div className="arm-overlay">
      <div className="arm-modal">

        {/* ── Header avec titre + croix ── */}
        <div className="arm-header-bar">
          <div className="arm-header-bar-title">
            <span className="arm-header-bar-label">Droits d'accès</span>
            <span className="arm-header-bar-user">{user.name} {user.lastname}</span>
          </div>
          <button
            type="button"
            className="arm-header-close"
            onClick={onClose}
            disabled={isSaving || isRevoking}
            title="Fermer"
          >
            <CloseOutlined />
          </button>
        </div>

        {error && (
          <div className="arm-error-banner">
            <WarningAmberOutlined />
            <span>{error}</span>
            <button type="button" className="arm-retry-link" onClick={fetchRights}>
              <RefreshOutlined />Réessayer
            </button>
          </div>
        )}

        {loading ? (
          <div className="arm-loading-state">
            <span className="arm-spinner arm-spinner--dark" />
            <span>Chargement des droits…</span>
          </div>
        ) : (
          <>
            <div className="arm-modules-header">
              <span className="arm-modules-title">MODULES</span>
              <span className="arm-modules-count">
                <span className="arm-modules-count-num">{enabledCount}</span>
                <span className="arm-modules-count-total">/ {modules.length} actifs</span>
              </span>
            </div>

            <div className="arm-modules-grid">
              {modules.map((mod) => {
                if (!permissions[mod.catalog_id]) return null;
                return (
                  <ModuleCard
                    key={mod.catalog_id}
                    catalogEntry={mod}
                    perm={permissions[mod.catalog_id]}
                    onChange={handleChange}
                    isSuperAdmin={isSuperAdmin}
                    onRequestDisable={handleRequestDisable}
                    frenchDepartmentsCatalog={frenchDepartmentsCatalog}
                  />
                );
              })}
            </div>
          </>
        )}

        <div className="arm-footer">
          <button type="button" className="arm-btn-cancel" onClick={onClose} disabled={isSaving || isRevoking}>
            Annuler
          </button>
          <button
            type="button"
            className={`arm-btn-save ${(!hasChanges || loading) ? 'arm-btn-save--idle' : ''}`}
            onClick={handleSave}
            disabled={isSaving || !hasChanges || loading}
          >
            {isSaving
              ? <><span className="arm-spinner" />Sauvegarde…</>
              : <><SaveOutlined />Enregistrer les droits</>
            }
          </button>
        </div>

        {disableConfirm && (
          <ConfirmPopup
            title={`Désactiver "${disableConfirm.label}" ?`}
            message={`L'utilisateur ${user.name} ${user.lastname} perdra l'accès au module "${disableConfirm.label}" ainsi qu'à toutes ses layers et restrictions associées.`}
            confirmLabel="Désactiver"
            onConfirm={confirmDisable}
            onCancel={() => setDisableConfirm(null)}
            isLoading={false}
          />
        )}

        {showRevoke && (
          <ConfirmPopup
            title="Révoquer tous les droits ?"
            message={`Cette action supprimera tous les droits d'accès de ${user.name} ${user.lastname}. L'utilisateur n'aura plus accès à aucun module. Cette action est irréversible.`}
            confirmLabel="Révoquer tous les droits"
            onConfirm={handleRevoke}
            onCancel={() => setShowRevoke(false)}
            isLoading={isRevoking}
          />
        )}

        {/* ── NOUVEAU : confirmation de la modification des droits avant envoi ── */}
   {pendingRightsSubmit && (
  <ConfirmPopup
    title="Confirmer la modification des droits"
    message={`Enregistrer les modifications des droits d'accès de ${user.name} ${user.lastname} ?`}
    confirmLabel="Confirmer"
    onConfirm={handleConfirmRightsSubmit}
    onCancel={handleCancelRightsSubmit}
    isLoading={isSaving}
    showIcon={false}
  />
)}

        {/* ── Avertissement config incomplète, avant sauvegarde ── */}
        {pendingWarnings && (
          <ConfirmPopup
            title="Configuration incomplète détectée"
            message={
              <div className="arm-warnings-summary">
                <p>Certains modules activés ont des axes non configurés :</p>
                <ul>
                  {pendingWarnings.map((entry) => (
                    <li key={entry.catalogId}>
                      <strong>{entry.label}</strong>
                      <ul>
                        {entry.warnings.map((w, i) => <li key={i}>{w}</li>)}
                      </ul>
                    </li>
                  ))}
                </ul>
                <p>Voulez-vous enregistrer quand même ?</p>
              </div>
            }
            confirmLabel="Enregistrer quand même"
            onConfirm={handleConfirmSaveAnyway}
            onCancel={handleCancelSaveWarnings}
            isLoading={isSaving}
          />
        )}

        {/* ── Confirmation d'envoi d'email après sauvegarde des droits (existant) ── */}
   {pendingRightsConfirm && (
  <ConfirmPopup
    title="Envoyer un email de notification ?"
    message={`Envoyer un email de mise à jour des droits à ${user.name} ${user.lastname} ?`}
    confirmLabel="Oui, envoyer l'email"
    cancelLabel="Non, ne pas envoyer"
    onConfirm={handleConfirmRightsEmail}
    onCancel={handleCancelRightsEmail}
    isLoading={false}
    showIcon={false}
  />
)}
      </div>
    </div>
  );
};

export default AccessRightsModal;